import { usePassportProvider } from "@/context";
import type { GameSpecifications } from "@/utils/cms/getGameSpecs";
import {
  Box,
  CloudImage,
  Divider,
  HeroBackground,
  Stack,
  mediaQuerySizesHelper,
  useTheme,
} from "@biom3/react";
import { SliceContext } from "@cms/context";
import { components } from "@cms/slices";
import { getPrismicDate, getWebLink } from "@cms/utils";
import type { Content } from "@prismicio/client";
import { SliceZone } from "@prismicio/react";
import type { WishlistProps } from "@ui-kit";
import { UnsubscribeFooter } from "@ui-kit/Unsubscribe";

import { SIDEBAR_WIDTH } from "@/constants";
import { useLoginModal } from "@/context/LoginModalContext";
import { GameIntro } from "@ui-kit/GameIntro";
import dynamic from "next/dynamic";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Quests } from "../Quests";

type MultiGameDetailsLayoutProps = {
  defaultBgImage: string | null | undefined;
  game: Content.GameDocument;
  page: Content.MultiGameDetailsPageDocument;
  gameSpecs: GameSpecifications;
  wishlistProps: WishlistProps;
};

// @NOTE: Right now, the PremiumGameHeader component uses screen size hooks
// internally, so it can only be client side rendered
const LazyPremiumGameHeader = dynamic(
  () =>
    import("@ui-kit/PremiumGameHeader").then((mod) => mod.PremiumGameHeader),
  { ssr: false },
);

export function MultiGameDetailsLayout({
  defaultBgImage,
  game,
  page,
  wishlistProps,
}: MultiGameDetailsLayoutProps) {
  const { t } = useTranslation();
  const { base } = useTheme();
  const { isLoggedIn } = usePassportProvider();
  const { openLoginModal } = useLoginModal();
  const gameContext = useMemo(() => ({ game }), [game]);

  const playLink = getWebLink(game.data.website).url;
  const mediaCarousel = page.data.slices.find(
    (slice) => slice.slice_type === "media_carousel",
  );
  const otherSlices = page.data.slices.filter(
    (slice) => slice.slice_type !== "media_carousel",
  );

  return (
    <>
      {defaultBgImage && (
        <HeroBackground
          sx={{
            pos: "absolute",
            top: "0",
            left: "0",
            bradtl: "base.borderRadius.x8",
            bradtr: "base.borderRadius.x8",
          }}
          aspectRatio={{ default: "1", xLarge: "16/11" }}
        >
          <HeroBackground.Image
            loading="eager"
            imageUrl={defaultBgImage}
            responsiveSizes={[
              256, 512, 1024, 1280, 1920, 2160, 2304, 2560, 2732, 2880, 3072,
              3200, 3456, 3840, 4096,
            ]}
            relativeImageSizeInLayout={mediaQuerySizesHelper([
              { rule: "default", width: "100dvw - 16px" },
              {
                rule: "minWidth",
                breakpoint: "medium",
                width: `calc(100dvw - ${SIDEBAR_WIDTH} - 32px)`,
              },
            ])}
          />
        </HeroBackground>
      )}
      <Stack
        sx={{ pos: "relative" }}
        testId="GameDetailPage__mainContainer"
        gap={{ default: "base.spacing.x2", medium: "base.spacing.x8" }}
      >
        <LazyPremiumGameHeader
          playLink={playLink}
          game={game}
          wishlistProps={wishlistProps}
        />
        <GameIntro
          gameId={game.uid}
          eventCountdown={getPrismicDate(page.data.event_countdown)}
          title={page.data.title || game.data.name || ""}
          description={page.data.description || game.data.name || ""}
          playLink={playLink}
          buttonSize="large"
          wishlistProps={wishlistProps}
          game={game}
          isLoggedIn={isLoggedIn ?? false}
          triggerLogin={openLoginModal}
        />
        <Quests
          game={game}
          show={!!wishlistProps.isWishlisted}
          wishlistProps={wishlistProps}
        />
        <Stack
          sx={{ px: { default: "none", large: "base.spacing.x12" } }}
          testId="heroSection"
          direction={{ default: "column", xLarge: "row" }}
          alignItems="flex-end"
          gap={{ default: "base.spacing.x8", large: "base.spacing.x12" }}
        >
          <Stack
            sx={{
              w: { default: "100%", xLarge: "72%" },
            }}
            testId="heroSection__leftSide"
          >
            <SliceContext.Provider
              value={{
                t,
                wishlistProps,
                data: {
                  game,
                },
              }}
            >
              {mediaCarousel ? (
                <SliceZone
                  components={components}
                  slices={[mediaCarousel]}
                  context={gameContext}
                />
              ) : null}
            </SliceContext.Provider>
            <Divider />
          </Stack>

          <Box
            sx={{ w: { default: "100%", xLarge: "28%" } }}
            testId="heroSection__rightSide"
          >
            {page.data.overlay_image.url ? (
              <CloudImage
                imageUrl={page.data.overlay_image.url}
                relativeImageSizeInLayout={mediaQuerySizesHelper([
                  { rule: "default", width: "90vw" },
                  { rule: "minWidth", breakpoint: "large", width: "37vw" },
                  { rule: "minWidth", breakpoint: "xxLarge", width: "32vw" },
                ])}
                responsiveSizes={[
                  512, 640, 720, 860, 1024, 1280, 1440, 1600, 1920, 2048, 2160,
                  2304, 2560, 2732, 2880,
                ]}
                testId="heroOverlayImage"
                sx={{
                  paddingTop: "base.spacing.x36",
                  // @TODO: for mobile, displaying these characters is alot harder (they have a different crop)
                  d: { default: "none", large: "block" },
                  w: {
                    default: "100%",
                    large: "clamp(250px, 36vw, 500px)",
                    xLarge: "clamp(300px, 36vw, 590px)",
                    xxLarge: "clamp(400px, 32vw, 650px)",
                    xxxLarge: "clamp(450px, 32vw, 720px)",
                  },
                  pos: { default: "static", large: "absolute" },
                  right: {
                    default: "unset",
                    large: `calc(${base.spacing.x10} * -1)`,
                  },
                  top: { default: "unset", large: "base.spacing.x10" },
                  objectFit: "cover",
                  objectPosition: "left center",
                  pointerEvents: "none",
                  userSelect: "none",
                }}
              />
            ) : null}
          </Box>
        </Stack>

        <Box testId="contentSection">
          <SliceContext.Provider
            value={{
              t,
              wishlistProps,
              data: {
                game,
              },
            }}
          >
            <SliceZone
              components={components}
              slices={otherSlices}
              context={gameContext}
            />
          </SliceContext.Provider>
        </Box>
      </Stack>

      <UnsubscribeFooter />
    </>
  );
}
